import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { THREAD_TAG_TYPE } from './constants';

export interface DraftConfig {
  strategy_id: number | null;
  region_ids: number[] | null;
  territory_ids: number[] | null;
  num_territories: number | null;
  granularity: 'state' | 'county' | 'zip_code' | null;
  metric_codes: string[] | null;
  metric_weights: Record<string, number> | null;
}

export interface Thread {
  id: number;
  title: string | null;
  status: 'open' | 'config_ready' | 'closed';
  draft_config: DraftConfig | null;
  created_at: string;
  updated_at: string;
}

export type AttachmentStatus =
  | 'uploading'
  | 'awaiting_mapping'
  | 'preview_ready'
  | 'committed'
  | 'error';

// Matches BE CustomerDatasetKind TextChoices values; emitted on dataset_kind
// fields by the WS consumer and surfaced through `kind` in tool responses.
export type AttachmentKind = 'metric' | 'rep_seed';

export interface AttachmentInfo {
  filename: string;
  // Optional: the BE does not persist a file size on thread attachments, so this
  // is only present on the optimistic copy of a just-uploaded attachment.
  size?: number;
  status: AttachmentStatus;
  dataset_id?: number;
  dataset_kind?: AttachmentKind | null;
  progress?: number;
  error?: string;
}

// Raw attachment shape as serialized by the BE (territory_optimization
// AttachmentSerializer, MKT-109): { id, name, kind, status }. Mapped to
// AttachmentInfo at the API boundary so chip-rendering components keep using FE
// field names. `kind` is null until the dataset's columns are mapped.
export interface ServerAttachment {
  id: number;
  name: string;
  kind: AttachmentKind | null;
  status: AttachmentStatus;
}

export function mapServerAttachment(att: ServerAttachment): AttachmentInfo {
  return {
    dataset_id: att.id,
    filename: att.name,
    dataset_kind: att.kind,
    status: att.status,
  };
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  sequence_number: number;
  created_at: string;
  job_id?: number;
  attachments?: AttachmentInfo[];
}

// Upload protocol error codes emitted by the WebSocket consumer on
// upload_rejected and upload_failed events. See medscout-api consumer
// territory_optimization_thread_consumer.py.
export type UploadErrorCode =
  | 'size_too_large'
  | 'mime_not_allowed'
  | 'upload_in_progress'
  | 'concurrency_limit'
  | 'invalid_upload_id'
  | 'invalid_payload'
  | 'size_mismatch'
  | 'no_active_upload'
  | 'parse_error'
  | 's3_error'
  | 'internal_error';

export interface UploadAckPayload {
  upload_id: string;
}

// BE sends `error` (a string code) on rejection/failure events; we surface it
// through UploadProtocolError.code on the FE side.
export interface UploadRejectedPayload {
  upload_id: string;
  error: UploadErrorCode;
}

export interface UploadFailedPayload {
  upload_id: string;
  error: UploadErrorCode;
}

export interface UploadCompletePayload {
  upload_id: string;
  dataset_id: number;
  status: AttachmentStatus;
  dataset_kind?: AttachmentKind | null;
}

export interface ThreadDetail extends Thread {
  messages: ChatMessage[];
  // True when metrics the thread's latest optimization run relied on have since
  // been removed from the strategy, making the optimization non-continuable. The
  // BE enforces this too (the agent rejects the turn); the FE blocks the send and
  // refinement affordances up front so the user never hits that dead end.
  // Detail-only — the list serializer does not include it. See MKT-402.
  has_stale_metrics: boolean;
}

// Raw thread-detail shape before attachment field-name mapping. Attachments
// arrive in the BE's ServerAttachment shape and are normalized in
// transformResponse below.
interface RawThreadMessage extends Omit<ChatMessage, 'attachments'> {
  attachments?: ServerAttachment[];
}

interface RawThreadDetail extends Omit<ThreadDetail, 'messages'> {
  messages: RawThreadMessage[];
}

export interface PaginatedThreadsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Thread[];
}

export const territoryChatApi = createApi({
  reducerPath: 'territoryChat',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [THREAD_TAG_TYPE],
  endpoints: (builder) => ({
    getThreads: builder.query<PaginatedThreadsResponse, void>({
      query: () => 'v1/territory-optimization/threads/',
      providesTags: [{ type: THREAD_TAG_TYPE, id: 'LIST' }],
    }),
    createThread: builder.mutation<Thread, { title?: string }>({
      query: (body) => ({
        url: 'v1/territory-optimization/threads/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: THREAD_TAG_TYPE, id: 'LIST' }],
    }),
    getThread: builder.query<ThreadDetail, number>({
      query: (id) => `v1/territory-optimization/threads/${id}/`,
      // Normalize the BE's attachment field names (id/name/kind) to the FE's
      // (dataset_id/filename/dataset_kind) so persisted chips render correctly
      // on reload without per-component mapping.
      transformResponse: (response: RawThreadDetail): ThreadDetail => ({
        ...response,
        messages: response.messages.map((m) => ({
          ...m,
          attachments: m.attachments?.map(mapServerAttachment),
        })),
      }),
      providesTags: (result) =>
        result ? [{ type: THREAD_TAG_TYPE, id: result.id }] : [],
    }),
    deleteThread: builder.mutation<void, number>({
      query: (id) => ({
        url: `v1/territory-optimization/threads/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: THREAD_TAG_TYPE, id: 'LIST' },
        { type: THREAD_TAG_TYPE, id },
      ],
    }),
    getThreadJobs: builder.query<{ count: number; results: Array<{ id: number }> }, number>({
      query: (id) => `v1/territory-optimization/threads/${id}/jobs/`,
    }),
    // User-reported issue with a chat thread. Slack-only on the BE (no model,
    // nothing to invalidate) and returns 204. See MKT-628.
    reportThreadIssue: builder.mutation<
      void,
      { threadId: number; description: string; url: string }
    >({
      query: ({ threadId, ...body }) => ({
        url: `v1/territory-optimization/threads/${threadId}/report-issue/`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetThreadsQuery,
  useCreateThreadMutation,
  useGetThreadQuery,
  useDeleteThreadMutation,
  useGetThreadJobsQuery,
  useLazyGetThreadJobsQuery,
  useReportThreadIssueMutation,
} = territoryChatApi;
